/**
 * Teams calendar quick-create deep link builder.
 * See docs/phase2/teams-calendar-quick-create-architecture.md §3, §4, §5.
 *
 * WHY the encoder is pinned: the `subject` this module emits becomes the Avoma meeting title,
 * which the backend fuzzy-matches against project_meetings.name_template to auto-link a
 * recording (§4.1). Any mutation of `subject` — a stray '+', a truncated tail, an added
 * prefix — silently breaks that match with no error surfaced anywhere (§4.2). Everything
 * below exists to keep `subject` byte-identical to name_template.
 */

const TEAMS_MEETING_NEW_BASE = 'https://teams.microsoft.com/l/meeting/new';

/** Architect safety margin, NOT a documented Teams/browser limit (§3.3). Applies to `content` only. */
export const CONTENT_MAX_CHARS = 900;

/** Fixed advisory body. Match-irrelevant by design — neither matcher reads it (§4.4). */
const CONTENT_BODY =
  'Created from DeliverPro. Keep the meeting title exactly as-is so the Avoma recording auto-links to this project.';

export const LABEL_DEFAULT =
  'Create Teams meeting with this exact title so the recording auto-links';

export const LABEL_SHARED =
  'Create Teams meeting — this title is shared across projects, so link the recording to this project manually afterwards';

export const LABEL_FALLBACK =
  'Create Teams meeting — this meeting has no Avoma title, so it will not auto-link and must be linked manually';

/**
 * Minimal shape needed to build a Teams calendar deep link.
 * Structurally satisfied by MeetingTimeline's file-local ProjectMeeting — deliberately
 * narrow so this module never depends on that (unexported) type.
 */
export interface TeamsDeepLinkInput {
  /** project_meetings.name_template — the canonical Avoma-matchable title. */
  readonly name_template: string | null | undefined;
  /** project_meetings.meeting_name — human label, fallback subject only. */
  readonly meeting_name: string;
  /**
   * project_meetings.meeting_topics — the meeting's expected topics, written into the invite body
   * as its agenda. Match-irrelevant (neither matcher reads the body).
   */
  readonly meeting_topics?: readonly string[] | null;
}

export interface TeamsDeepLink {
  /** Fully-encoded https://teams.microsoft.com/l/meeting/new URL. */
  readonly href: string;
  /** The exact, unencoded subject sent to Teams. Verbatim name_template unless usesFallback. */
  readonly subject: string;
  /** true when name_template was empty and meeting_name was substituted (§4.2c). */
  readonly usesFallback: boolean;
  /** Single string used for BOTH aria-label and visible tooltip text (WCAG 2.5.3). */
  readonly label: string;
}

/**
 * Five-step rule from §3.3. Result length is at most `max + 1` — the ellipsis is appended
 * after the cut, not substituted into the budget.
 */
export function truncateAtWordBoundary(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const i = slice.lastIndexOf(' ');
  const cut = i > 0 ? slice.slice(0, i) : slice;
  return `${cut.trimEnd()}\u2026`;
}

/**
 * True only for the §4.3 collapse condition: a subject with NO ' | ' segments that names an
 * Implementation Plan Review. Such a template carries no project-distinguishing token, so
 * buildMatcher degrades to a whole-string substring match and the backend's Map key can
 * collapse across projects. A subject that DOES carry pipe segments is distinguishable.
 */
function isSharedRecurringTemplate(subject: string): boolean {
  return !subject.includes(' | ') && /implementation plan review/i.test(subject);
}

/**
 * Invite body: an "Agenda" list of the expected topics, then the fixed advisory sentence. Plain
 * text — the Teams `content` parameter does not render HTML. Topics are dropped from the end (never
 * cut mid-topic) so the body stays within CONTENT_MAX_CHARS with the advisory sentence intact.
 */
export function buildTeamsContent(topics?: readonly string[] | null): string {
  const agenda = (topics ?? []).map((t) => t.trim()).filter((t) => t.length > 0);
  if (agenda.length === 0) return CONTENT_BODY;
  const suffix = `\n\n${CONTENT_BODY}`;
  const lines: string[] = [];
  for (const topic of agenda) {
    const candidate = ['Agenda:', ...lines, `\u2022 ${topic}`].join('\n') + suffix;
    if (candidate.length > CONTENT_MAX_CHARS) break;
    lines.push(`\u2022 ${topic}`);
  }
  if (lines.length === 0) return CONTENT_BODY;
  if (lines.length < agenda.length) lines.push('\u2022 \u2026');
  return ['Agenda:', ...lines].join('\n') + suffix;
}

export function buildTeamsDeepLink(input: TeamsDeepLinkInput): TeamsDeepLink | null {
  const template = (input.name_template ?? '').trim();
  const usesFallback = template.length === 0;
  const subject = usesFallback ? input.meeting_name.trim() : template;

  if (subject.length === 0) return null;

  // encodeURIComponent ONLY. URLSearchParams is prohibited here — it serialises space as '+',
  // which Teams shows literally and which breaks buildMatcher's space-separated comparison (§3.2).
  const encodedSubject = encodeURIComponent(subject);

  const query =
    encodedSubject.length > CONTENT_MAX_CHARS
      ? // Over budget: drop `content` entirely. `subject` is NEVER shortened (§3.3, §4.2b).
        `subject=${encodedSubject}`
      : `subject=${encodedSubject}&content=${encodeURIComponent(
          truncateAtWordBoundary(buildTeamsContent(input.meeting_topics), CONTENT_MAX_CHARS),
        )}`;

  const label = usesFallback
    ? LABEL_FALLBACK
    : isSharedRecurringTemplate(subject)
      ? LABEL_SHARED
      : LABEL_DEFAULT;

  return { href: `${TEAMS_MEETING_NEW_BASE}?${query}`, subject, usesFallback, label };
}
